// A place on the map, typed in degrees and held in millionths.
//
// The server keeps a shop's latitude and longitude as whole millionths of a
// degree (UC-33), and the owner types degrees, as Google Maps shows them. The
// two meet here, by string rather than by multiplying, so 30.044420 is
// 30044420 exactly. The server checks the ranges again; this only lets the
// form say what is wrong before anything is sent.

export type DegreesProblem = 'empty' | 'form' | 'range';

/**
 * Read typed degrees into millionths. Arabic digits and the Arabic decimal
 * mark count, as an Arabic keyboard types them. A seventh decimal rounds.
 * limit is 90 for a latitude and 180 for a longitude.
 */
export function readDegrees(
	typed: string,
	limit: number,
): { e6: number; problem?: undefined } | { e6?: undefined; problem: DegreesProblem } {
	const text = typed
		.trim()
		.replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x660))
		.replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x6f0))
		.replace(/[٫,]/g, '.')
		.replace(/−/g, '-');
	if (text === '') return { problem: 'empty' };
	const match = /^(-?)(\d{1,3})(?:\.(\d+))?$/.exec(text);
	if (!match) return { problem: 'form' };
	const [, sign, whole, fraction = ''] = match;
	const digits = (fraction + '0000000').slice(0, 7);
	let e6 = Number(whole) * 1_000_000 + Number(digits.slice(0, 6));
	if (Number(digits[6]) >= 5) e6 += 1;
	if (e6 > limit * 1_000_000) return { problem: 'range' };
	return { e6: sign === '-' && e6 !== 0 ? -e6 : e6 };
}

/** Millionths as degrees with six places: 30044420 reads "30.044420". */
export function formatDegrees(e6: number): string {
	const negative = e6 < 0;
	const digits = Math.abs(Math.trunc(e6)).toString().padStart(7, '0');
	const text = `${digits.slice(0, -6)}.${digits.slice(-6)}`;
	return negative ? `-${text}` : text;
}
